import { FC, useCallback, useContext, useRef } from 'react'
import { Modal, Button } from 'react-bootstrap'

import DocumentsCarousel from './documents-carousel'

import { AppContext } from '../../../app-context'
import { showErrorAlert } from '../../alert'
import { triggerBrowserDownload } from '../../../util/file'
import useFetchDocumentContent from './use-fetch-document-content'
import { IDocument } from '../../../interfaces'
import { FetchDocumentContent } from './document-upload-context-types'

type DocumentsPreviewModalProps = {
  documents?: IDocument[]
  currentCarouselDocument?: IDocument
  setCurrentCarouselDocument?: (document: IDocument) => void
  parentType?: string
  isDocumentsPreviewModalOpen?: boolean
  toggleDocumentsPreviewModal?: () => void
  fetchDocumentContent?: FetchDocumentContent
}

const DocumentsPreviewModal: FC<DocumentsPreviewModalProps> = ({
  documents,
  currentCarouselDocument,
  setCurrentCarouselDocument,
  parentType,
  isDocumentsPreviewModalOpen,
  toggleDocumentsPreviewModal,
  fetchDocumentContent: _fetchDocumentContent,
}) => {
  const { dispatch } = useContext(AppContext)

  const defaultFetchDocumentContent = useFetchDocumentContent()
  const resolvedFetchDocumentContent = _fetchDocumentContent ?? defaultFetchDocumentContent

  const fetchedContentRef = useRef(new Map<number, Promise<Blob>>())

  const fetchDocumentContent: FetchDocumentContent = useCallback(
    (document, options) => {
      const cached = fetchedContentRef.current.get(document.id)

      if (cached) {
        return cached
      }

      const contentPromise = resolvedFetchDocumentContent(document, options).catch(err => {
        // Don't cache a failure — let reopening the preview retry instead of replaying the same error.
        fetchedContentRef.current.delete(document.id)
        throw err
      })
      fetchedContentRef.current.set(document.id, contentPromise)

      return contentPromise
    },
    [resolvedFetchDocumentContent],
  )

  return (
    <Modal size="lg" show={isDocumentsPreviewModalOpen} onHide={toggleDocumentsPreviewModal} animation={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>{currentCarouselDocument?.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body className={parentType === 'Journal' ? 'journal-viewer' : ''}>
        {currentCarouselDocument && (
          <DocumentsCarousel
            documents={documents ?? []}
            currentDocument={currentCarouselDocument}
            setCurrentDocument={setCurrentCarouselDocument !== undefined ? setCurrentCarouselDocument : () => {}}
            fetchDocumentContent={fetchDocumentContent}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={toggleDocumentsPreviewModal}>
          Cancel
        </Button>

        {currentCarouselDocument && (
          <Button
            variant="primary"
            onClick={() =>
              fetchDocumentContent(currentCarouselDocument, { download: true })
                .then(blob => triggerBrowserDownload(blob, currentCarouselDocument.name))
                .catch(err => {
                  console.warn('Something went wrong during document download', err)
                  dispatch(showErrorAlert('Something went wrong during document download'))
                })
            }
          >
            Download File
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default DocumentsPreviewModal
